import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { FileEdit, Eye, CheckCircle2 } from 'lucide-react';
import { useLocalization } from '@/hooks/useLocalization';

const ApprovalWorkflowSummary = () => {
  const { tx, t } = useLocalization();
  const [counts, setCounts] = useState({ draft: 0, pending: 0, approved: 0 });

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('ai_outputs').select('status');
      if (data) {
        setCounts({
          draft: data.filter((d: { status: string }) => d.status === 'draft').length,
          pending: data.filter((d: { status: string }) => d.status === 'pending_review').length,
          approved: data.filter((d: { status: string }) => ['approved', 'corrected'].includes(d.status)).length,
        });
      }
    };
    load();
    const channel = supabase
      .channel('approval-summary')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_outputs' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const stages = [
    { label: 'Draft', icon: FileEdit, count: counts.draft, color: 'bg-muted text-muted-foreground' },
    { label: 'Reviewed', icon: Eye, count: counts.pending, color: 'bg-amber-500/10 text-amber-700 dark:text-amber-400' },
    { label: 'Approved', icon: CheckCircle2, count: counts.approved, color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Approval Workflow')}</CardTitle>
        <CardDescription>{t('Draft → Reviewed → Approved pipeline for every AI output')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          {stages.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="flex items-center flex-1">
                <div className={`flex-1 rounded-lg p-4 ${s.color}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-medium uppercase tracking-wide">{tx(s.label)}</span>
                  </div>
                  <p className="text-2xl font-bold">{s.count}</p>
                </div>
                {i < stages.length - 1 && <div className="w-6 h-px bg-border mx-1" />}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ApprovalWorkflowSummary;
